// @flow

import * as constants from './constants'

import type { PlayerStateType, PlayerActionType } from './types'

export const INITIAL_STATE: PlayerStateType = {
  ready: false,
  loading: false,
  playing: false,
  paused: false,
  stopped: true,
  seeking: false,
  src: '',
  error: null,
  time: {
    current: 0,
    duration: 0,
  },
  audioTracks: [],
  subtitleTracks: [],
  recovery: constants.RECOVERY_PHASES.LOADING,
}

// The error stays while the error modal is shown, even if the engine fires canplay or play behind it
const keepRecoveryError = (state: PlayerStateType) =>
  state.recovery === constants.RECOVERY_PHASES.FAILED ? state.error : null

const reducer = (state: PlayerStateType, action: PlayerActionType): PlayerState =>
  ({
    [constants.LOAD_META]: () => {
      const { time, audioTracks, subtitleTracks } = action.payload

      return {
        ...state,
        time,
        audioTracks,
        subtitleTracks,
      }
    },
    [constants.LOADING]: () => {
      const { loading } = action.payload

      return { ...state, loading }
    },
    [constants.SEEKING]: () => {
      const { loading, seeking } = action.payload

      return { ...state, loading, seeking }
    },
    [constants.CAN_PLAY]: () => ({
      ...state,
      ready: true,
      loading: false,
      error: keepRecoveryError(state),
    }),
    [constants.SOURCE_LOADED]: () => ({
      ...state,
      src: action.payload.src,
    }),
    [constants.PLAY]: () => ({
      ...state,
      playing: true,
      paused: false,
      stopped: false,
      error: keepRecoveryError(state),
    }),
    [constants.PAUSE]: () => ({
      ...state,
      playing: false,
      paused: true,
    }),
    [constants.END]: () => ({
      ...state,
      playing: false,
      paused: false,
      stopped: true,
      time: {
        ...state.time,
        current: 0,
      },
    }),
    [constants.ERROR]: () => {
      const { message, critical = false } = action.payload.error
      const code = action.payload.error?.code ?? null

      return {
        ...state,
        loading: false,
        ...(critical && {
          playing: false,
          paused: false,
          stopped: true,
        }),
        error: {
          message,
          // An engine message or code the interface puts into a localized sentence
          code,
          critical,
        },
      }
    },
    [constants.CHANGE_AUDIO_TRACKS]: () => ({
      ...state,
      audioTracks: action.payload.audioTracks,
    }),
    [constants.CHANGE_SUBTITLE_TRACKS]: () => ({
      ...state,
      subtitleTracks: action.payload.subtitleTracks,
    }),
    [constants.RESET_ERROR]: () => ({
      ...state,
      error: null,
    }),
    [constants.RECOVERY]: () => ({
      ...state,
      recovery: action.payload.recovery,
    }),
    [constants.RESET]: () => INITIAL_STATE,
  }[action.type]())

export default reducer
